import { useEffect } from 'react'
import { ThemeProvider } from 'styled-components'
import { StoreProvider, useStoreActions } from 'easy-peasy'
import { useTranslation } from 'react-i18next'
import { ConfigProvider } from 'antd'
import Script from 'next/script'
import { useRouter } from 'next/router'

import * as dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import isBetween from 'dayjs/plugin/isBetween'
import 'dayjs/locale/th'
import _find from 'lodash/find'
// import * as Sentry from '@sentry/react'
// import { BrowserTracing } from '@sentry/tracing'

import { languages } from 'locales/i18n'

import * as gtag from 'lib/gtag'
import { pageview } from 'lib/gtm'

import store from 'store'

import GlobalStyle from 'style/global'
import theme from 'style/theme'

import 'antd/dist/antd.css'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'style/font.scss'
import 'style/index.scss'

dayjs.extend(customParseFormat)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isBetween)

dayjs.locale('th')
dayjs.tz.setDefault(Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Bangkok')

// if (process.env.NEXT_PUBLIC_SENTRY_DSN && process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT) {
//   Sentry.init({
//     dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
//     integrations: [new BrowserTracing()],
//     tracesSampleRate: 1.0,
//     environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
//   })
// }

const AfterStoreProvider = ({ Component, pageProps }) => {
  const getProfile = useStoreActions(actions => actions.auth.getProfile)

  useEffect(() => {
    getProfile()
  }, [])

  return <Component {...pageProps} />
}

export default function MyApp(props) {
  const router = useRouter()
  const { i18n } = useTranslation()
  const isProduction = process.env.NEXT_PUBLIC_ENV === 'production'
  const gaId = process.env.NEXT_PUBLIC_GA_ID_NEW
  const gtmId = process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID_NEW
  const cookieWowId = process.env.NEXT_PUBLIC_COOKIE_WOW

  const currentLang = _find(languages, lang => lang.key === i18n.language)

  useEffect(() => {
    const handleRouteChange = url => {
      gtag.pageview(url)
      pageview(url)
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  return (
    <>
      {/* Hotjar Tracking Code for fix.24x.co.th */}
      {/* {process.env.NEXT_PUBLIC_ENV === 'production' && (
        <Script
          id="hotjar"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
          (function(h,o,t,j,a,r){
            h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
            h._hjSettings={hjid:1735213,hjsv:6};
            a=o.getElementsByTagName('head')[0];
            r=o.createElement('script');r.async=1;
            r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
            a.appendChild(r);
          })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
          `,
          }}
        />
      )} */}

      {/* Global Site Tag (gtag.js) - Google Analytics NEW */}
      {isProduction && gaId && (
        <>
          <Script
            strategy="lazyOnload"
            src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
          />
          <Script
            id="gtag-new-init"
            strategy="lazyOnload"
            dangerouslySetInnerHTML={{
              __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${gaId}', {
              page_path: window.location.pathname,
            });
          `,
            }}
          />
        </>
      )}

      {/* Google Tag Manager - Global base code NEW */}
      {isProduction && gtmId && (
        <Script
          id="gtm-new-init"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer', '${gtmId}');
          `,
          }}
        />
      )}

      {/* <!-- Google Tag Manager (noscript) NEW --> */}
      {isProduction && gtmId && (
        <noscript>
          <iframe
            title="Google Tag Manager"
            src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>
      )}

      {/* elasticApmRum */}
      {/* <Script
        id="elastic-apm-rum"
        dangerouslySetInnerHTML={{
          __html: `
          elasticApm.init({
            serviceName: '${process.env.NEXT_PUBLIC_APM_SERVICE_NAME}',
            serverUrl: '${process.env.NEXT_PUBLIC_APM_SERVICE_URL}',
          })
          `,
        }}
      /> */}

      <Script
        src={`https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_MAP_API_KEY}&libraries=places,drawing,geometry&language=th&region=th`}
        strategy="afterInteractive"
      />

      {isProduction && cookieWowId && router.pathname !== '/fixer/register' && (
        <>
          <Script src="https://cookiecdn.com/cwc.js" strategy="lazyOnload" />
          <Script
            id="cookieWow"
            src={`https://cookiecdn.com/configs/${cookieWowId}`}
            data-cwcid={cookieWowId}
            strategy="lazyOnload"
          />
        </>
      )}

      <StoreProvider store={store}>
        <GlobalStyle />
        <ThemeProvider theme={theme}>
          <ConfigProvider locale={currentLang.antdLocale}>
            <AfterStoreProvider {...props} />
          </ConfigProvider>
        </ThemeProvider>
      </StoreProvider>
    </>
  )
}
